import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { OAuthClientRequest, OAuthClientResponse, OAuthClientType, OAuthClientsApi } from '@venecia/apis/auth';
import { Account, AccountApi, Application, ApplicationApi, Client, ClientApi, ClientRequest, ClientType, Role, RoleApi } from '@venecia/apis/identity';
import { InputBlock } from '@venecia/molecules';
import { ConfirmDialogService, ErrorMessageService, MessageService, TraceService } from '@venecia/services';

@Component({
  selector: 'app-client-modal',
  imports: [CommonModule, InputBlock, ReactiveFormsModule, TranslateModule],
  templateUrl: './client-modal.html',
  styleUrl: './client-modal.scss',
})
export class ClientModal implements OnChanges {
  private readonly accountApi = inject(AccountApi);
  private readonly applicationApi = inject(ApplicationApi);
  private readonly clientApi = inject(ClientApi);
  private readonly oauthClientsApi = inject(OAuthClientsApi);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly roleApi = inject(RoleApi);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  @Input() client: Client | null = null;
  @Input() open = false;
  @Input() preselectedAccountId: number | null = null;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<void>();

  readonly availableAccounts = signal<readonly Account[]>([]);
  readonly availableApplications = signal<readonly Application[]>([]);
  readonly availableRoles = signal<readonly Role[]>([]);
  readonly authorizationClient = signal<OAuthClientResponse | null>(null);
  readonly authorizationBusy = signal(false);
  readonly deleting = signal(false);
  readonly detailMode = signal<'create' | 'edit'>('create');
  readonly roles = signal<readonly Role[]>([]);
  readonly saving = signal(false);
  readonly selectedAccountId = signal<number | null>(null);
  readonly selectedApplicationId = signal<number | null>(null);
  readonly selectedOAuthClientType = signal<OAuthClientType>('CONFIDENTIAL');
  readonly selectedRoleId = signal<number | null>(null);
  readonly submitted = signal(false);

  readonly selectedCreatedAt = signal<string | null>(null);
  readonly selectedId = signal<number | null>(null);
  readonly selectedUpdatedAt = signal<string | null>(null);

  readonly isCreateMode = computed(() => this.detailMode() === 'create');
  readonly isEditMode = computed(() => this.detailMode() === 'edit');
  readonly selectedAccount = computed(
    () => this.availableAccounts().find((account) => account.id === this.selectedAccountId()) ?? null,
  );
  readonly selectedApplication = computed(
    () => this.availableApplications().find((application) => application.id === this.selectedApplicationId()) ?? null,
  );
  readonly isOwnerAccount = computed(() => Boolean(this.selectedAccount()?.owner));
  readonly canChooseType = computed(() => this.isCreateMode() && this.isOwnerAccount());
  readonly oauthClientType = computed<OAuthClientType>(() => {
    if (this.isEditMode()) {
      return this.authorizationClient()?.['client-type'] ?? this.selectedOAuthClientType();
    }

    if (!this.isOwnerAccount()) {
      return 'CONFIDENTIAL';
    }

    return this.selectedOAuthClientType();
  });
  readonly oauthEnabled = computed(() => this.authorizationClient()?.enabled ?? false);
  readonly oauthToggleDisabled = computed(
    () => this.saving() || this.deleting() || this.authorizationBusy() || !this.authorizationClient()?.id,
  );
  readonly oauthTypeLabelKey = computed(() => `pages.clients.types.${this.oauthClientType().toLowerCase()}`);
  readonly selectableRoles = computed(() => {
    const selectedIds = new Set(
      this.roles()
        .map((role) => role.id)
        .filter((roleId): roleId is number => roleId !== null),
    );

    return this.availableRoles().filter((role) => role.id !== null && !selectedIds.has(role.id));
  });

  readonly form = this.formBuilder.nonNullable.group({
    description: ['', [Validators.maxLength(512)]],
    clientId: ['', [Validators.required, Validators.maxLength(64), Validators.pattern(/^[a-z-]+$/)]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || (!changes['client'] && !changes['open'])) {
      return;
    }

    this.configureFromInputs();
  }

  closeDetailModal(force = false): void {
    if (!force && (this.saving() || this.deleting() || this.authorizationBusy())) {
      return;
    }

    this.closed.emit();
  }

  async requestDelete(): Promise<void> {
    const id = this.selectedId();
    if (id === null) {
      return;
    }

    const keys = [
      'pages.clients.confirm-delete.cancel',
      'pages.clients.confirm-delete.confirm',
      'pages.clients.confirm-delete.message',
      'pages.clients.confirm-delete.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    const confirmed = await this.confirmDialogService.open({
      cancelText: i18n['pages.clients.confirm-delete.cancel'],
      confirmText: i18n['pages.clients.confirm-delete.confirm'],
      message: i18n['pages.clients.confirm-delete.message'],
      title: i18n['pages.clients.confirm-delete.title'],
    });

    if (!confirmed) {
      return;
    }

    this.deleting.set(true);
    try {
      if (this.authorizationClient()?.id !== undefined) {
        await firstValueFrom(this.oauthClientsApi.deleteById(this.authorizationClient()!.id));
      }

      await firstValueFrom(this.clientApi.delete(id));
      this.messageService.success(await this.translate('pages.clients.messages.delete-success'));
      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.delete-error'));
    } finally {
      this.deleting.set(false);
    }
  }

  async save(): Promise<void> {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isCreateMode()) {
      const application = this.selectedApplication();
      if (!application) {
        this.messageService.error(await this.translate('pages.clients.validation.application-required'));
        return;
      }

      if (!application.key?.trim()) {
        this.messageService.error(await this.translate('pages.clients.validation.application-key-required'));
        return;
      }

      if (!application['site-url']?.trim()) {
        this.messageService.error(await this.translate('pages.clients.validation.application-site-url-required'));
        return;
      }
    }

    const confirmed = await this.confirmSave();
    if (!confirmed) {
      return;
    }

    this.saving.set(true);
    try {
      const payload = this.toApiModel();
      if (this.isCreateMode()) {
        const createdClient = (await firstValueFrom(this.clientApi.create(payload))).body;
        await this.createAuthorizationClient(createdClient['client-id']);
        this.messageService.success(await this.translate('pages.clients.messages.create-success'));
      } else {
        await firstValueFrom(this.clientApi.update(this.selectedIdOrThrow(), payload));
        if (this.oauthClientType() === 'CONFIDENTIAL') {
          await this.persistRolesOnSave();
        }
        this.messageService.success(await this.translate('pages.clients.messages.update-success'));
      }

      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      if (error instanceof OAuthClientCreateError) {
        this.messageService.error(await this.errorMessageService.resolveApiError(error.cause, 'pages.clients.oauth-create-error'));
      } else {
        this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.save-error'));
      }
    } finally {
      this.saving.set(false);
    }
  }

  async onAuthorizationEnabledChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const shouldEnable = input?.checked ?? false;
    const authorizationClient = this.authorizationClient();

    if (!authorizationClient?.id) {
      if (input) {
        input.checked = this.oauthEnabled();
      }
      return;
    }

    this.authorizationBusy.set(true);
    try {
      if (shouldEnable) {
        await firstValueFrom(this.oauthClientsApi.enable(authorizationClient.id));
        this.authorizationClient.set({ ...authorizationClient, enabled: true });
        this.messageService.success(await this.translate('pages.clients.messages.oauth-enable-success'));
      } else {
        await firstValueFrom(this.oauthClientsApi.disable(authorizationClient.id));
        this.authorizationClient.set({ ...authorizationClient, enabled: false });
        this.messageService.success(await this.translate('pages.clients.messages.oauth-disable-success'));
      }
    } catch (error) {
      this.traceService.internal(error, true);
      if (input) {
        input.checked = this.oauthEnabled();
      }
      const key = shouldEnable ? 'pages.clients.oauth-enable-error' : 'pages.clients.oauth-disable-error';
      this.messageService.error(await this.errorMessageService.resolveApiError(error, key));
    } finally {
      this.authorizationBusy.set(false);
    }
  }

  hasError(controlName: keyof ClientFormModel, errorName: string): boolean {
    return this.form.controls[controlName].hasError(errorName);
  }

  addSelectedRole(): void {
    const selectedRoleId = this.selectedRoleId();
    if (selectedRoleId === null) {
      return;
    }

    const roleToAdd = this.availableRoles().find((role) => role.id === selectedRoleId);
    if (!roleToAdd) {
      return;
    }

    const alreadySelected = this.roles().some((role) => role.id === selectedRoleId);
    if (alreadySelected) {
      return;
    }

    this.roles.set([...this.roles(), roleToAdd]);
    this.selectedRoleId.set(null);
  }

  onRoleSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedRoleId.set(value ? Number(value) : null);
  }

  onAccountSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedAccountId.set(value ? Number(value) : null);
    this.applyTypeRulesForCurrentAccount();
  }

  onTypeSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value;

    if (value === 'PUBLIC' || value === 'CONFIDENTIAL') {
      this.selectedOAuthClientType.set(value);
    } else {
      this.selectedOAuthClientType.set('CONFIDENTIAL');
    }
  }

  onApplicationSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedApplicationId.set(value ? Number(value) : null);
    this.roles.set([]);
    this.selectedRoleId.set(null);
    this.availableRoles.set([]);
    void this.loadRoles();
  }

  isInvalid(controlName: keyof ClientFormModel): boolean {
    const control = this.form.controls[controlName];
    return (control.dirty || control.touched || this.submitted()) && control.invalid;
  }

  removeRole(roleId: number): void {
    const updatedRoles = this.roles().filter((role) => role.id !== roleId);
    this.roles.set(updatedRoles);
  }

  selectAllRoles(): void {
    this.roles.set(
      this.availableRoles().filter((role) => role.id !== null),
    );
    this.selectedRoleId.set(null);
  }

  unselectAllRoles(): void {
    this.roles.set([]);
    this.selectedRoleId.set(null);
  }

  private configureFromInputs(): void {
    this.submitted.set(false);
    this.authorizationClient.set(null);

    const client = this.client;
    if (!client) {
      this.availableAccounts.set([]);
      this.availableApplications.set([]);
      this.availableRoles.set([]);
      this.detailMode.set('create');
      this.selectedId.set(null);
      this.selectedCreatedAt.set(null);
      this.selectedAccountId.set(this.preselectedAccountId);
      this.selectedApplicationId.set(null);
      this.selectedOAuthClientType.set('CONFIDENTIAL');
      this.selectedRoleId.set(null);
      this.selectedUpdatedAt.set(null);
      this.roles.set([]);

      this.form.reset({
        clientId: '',
        description: '',
      });
      void this.loadAccounts();
      void this.loadApplications();
      return;
    }

    this.detailMode.set('edit');
    this.selectedId.set(client.id);
    this.selectedCreatedAt.set(client['created-at'] ?? null);
    this.selectedUpdatedAt.set(client['updated-at'] ?? null);
    this.roles.set(client.roles ?? []);
    this.selectedAccountId.set(client.account?.id ?? null);
    this.selectedApplicationId.set(null);
    this.selectedOAuthClientType.set((client.type === 'PUBLIC' ? 'PUBLIC' : 'CONFIDENTIAL') as OAuthClientType);
    this.selectedRoleId.set(null);

    this.form.reset({
      clientId: client['client-id'],
      description: client.description ?? '',
    });

    void this.loadAccounts();
    void this.loadApplications();
    void this.loadRoles();
    void this.loadAuthorizationClientByClientId(client['client-id']);
  }

  private selectedIdOrThrow(): number {
    const id = this.selectedId();
    if (id === null) {
      throw new Error('Client id is required for update');
    }
    return id;
  }

  private toApiModel(): ClientRequest {
    const formValue = this.form.getRawValue();
    const accountId = this.selectedAccountId();
    if (accountId === null) {
      throw new Error('Account id is required for client create/update');
    }

    return {
      account: { id: accountId },
      'client-id': formValue.clientId.trim(),
      description: formValue.description.trim(),
      type: this.oauthClientType() as ClientType,
    };
  }

  private toOAuthClientRequest(clientId: string): OAuthClientRequest {
    const application = this.selectedApplication();
    if (!application?.key?.trim()) {
      throw new Error('Application key is required for OAuth client create');
    }

    if (!application['site-url']?.trim()) {
      throw new Error('Application site url is required for OAuth client create');
    }

    return {
      'client-type': this.oauthClientType(),
      'client-id': clientId,
      'application-key': application.key.trim(),
      'redirect-uri': application['site-url'].trim(),
    };
  }

  private async createAuthorizationClient(clientId: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.oauthClientsApi.create(this.toOAuthClientRequest(clientId)));
      this.authorizationClient.set(response.body);
      await this.showOneTimeCredential(response.body['plain-credential']);
    } catch (error) {
      this.traceService.internal(error, true);
      throw new OAuthClientCreateError(error);
    }
  }

  private async loadAuthorizationClientByClientId(clientId: string): Promise<void> {
    const normalizedClientId = clientId.trim();
    if (!normalizedClientId) {
      return;
    }

    this.authorizationBusy.set(true);
    try {
      const response = await firstValueFrom(this.oauthClientsApi.findByClientId(normalizedClientId));
      this.authorizationClient.set(response.body);
      this.selectedApplicationId.set(this.resolveApplicationIdByKey(response.body['application-key']));
      await this.loadRoles();
    } catch (error) {
      this.traceService.internal(error, true);
      this.authorizationClient.set(null);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.oauth-load-error'));
    } finally {
      this.authorizationBusy.set(false);
    }
  }

  private resolveApplicationIdByKey(applicationKey?: string): number | null {
    const normalizedKey = applicationKey?.trim();
    if (!normalizedKey) {
      return null;
    }

    return this.availableApplications().find((application) => application.key === normalizedKey)?.id ?? null;
  }

  private async showOneTimeCredential(plainCredential?: string): Promise<void> {
    const credential = plainCredential?.trim();
    if (!credential) {
      return;
    }

    const keys = [
      'pages.clients.credential-dialog.cancel',
      'pages.clients.credential-dialog.confirm',
      'pages.clients.credential-dialog.message',
      'pages.clients.credential-dialog.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    await this.confirmDialogService.open({
      cancelText: i18n['pages.clients.credential-dialog.cancel'],
      confirmText: i18n['pages.clients.credential-dialog.confirm'],
      details: credential,
      message: i18n['pages.clients.credential-dialog.message'],
      size: 'lg',
      title: i18n['pages.clients.credential-dialog.title'],
    });
  }

  private async loadAccounts(): Promise<void> {
    try {
      const response = await firstValueFrom(this.accountApi.search());
      this.availableAccounts.set(response.body.data ?? []);
      this.applyTypeRulesForCurrentAccount();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.accounts-load-error'));
    }
  }

  private async loadApplications(): Promise<void> {
    try {
      const response = await firstValueFrom(this.applicationApi.search());
      this.availableApplications.set(response.body.data ?? []);

      if (this.authorizationClient()?.['application-key']) {
        this.selectedApplicationId.set(this.resolveApplicationIdByKey(this.authorizationClient()?.['application-key']));
        void this.loadRoles();
      }
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.applications-load-error'));
    }
  }

  private async loadRoles(): Promise<void> {
    const applicationId = this.selectedApplicationId();
    if (applicationId === null) {
      this.availableRoles.set([]);
      return;
    }

    try {
      const response = await firstValueFrom(this.roleApi.search({
        payload: {
          filters: [
            {
              field: 'application.id',
              value: applicationId,
              comparative: 'eq',
            },
          ],
        },
      }));
      this.availableRoles.set(response.body.data ?? []);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.roles-load-error'));
    }
  }

  private applyTypeRulesForCurrentAccount(): void {
    if (this.isEditMode()) {
      return;
    }

    if (!this.isOwnerAccount()) {
      this.selectedOAuthClientType.set('CONFIDENTIAL');
    }
  }

  private toRoleIdPayload(roles: readonly Role[]): readonly { readonly id: number }[] {
    return roles
      .map((role) => role.id)
      .filter((roleId): roleId is number => roleId !== null)
      .map((roleId) => ({ id: roleId }));
  }

  private async persistRolesOnSave(): Promise<void> {
    const clientId = this.selectedId();
    if (clientId === null) {
      return;
    }

    try {
      await firstValueFrom(this.clientApi.updateRoles(clientId, this.toRoleIdPayload(this.roles())));
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.clients.roles-update-error'));
      throw error;
    }
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    return firstValueFrom(this.translateService.get([...keys]));
  }

  private async confirmSave(): Promise<boolean> {
    const keys = [
      'common.confirm-save.cancel',
      'common.confirm-save.confirm',
      'common.confirm-save.message',
      'common.confirm-save.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    return this.confirmDialogService.open({
      cancelText: i18n['common.confirm-save.cancel'],
      confirmText: i18n['common.confirm-save.confirm'],
      message: i18n['common.confirm-save.message'],
      title: i18n['common.confirm-save.title'],
    });
  }
}

interface ClientFormModel {
  clientId: string;
  description: string;
}

class OAuthClientCreateError extends Error {
  constructor(public override readonly cause: unknown) {
    super('OAuth client create failed');
  }
}
